import { BookOpen, Calendar, Clock, Layers } from 'lucide-react';

interface TimetableSummaryCardsProps {
  totalSubjects: number;
  totalPeriods: number;
  /** `'—'` until the week has loaded. */
  daysInWeek: number | string;
  /**
   * The share of this teacher's own periods they have not marked unavailable —
   * not a class's attendance, which this screen never reads.
   */
  attendance?: string;
}

export function TimetableSummaryCards({
  totalSubjects,
  totalPeriods,
  daysInWeek,
  attendance = '—',
}: TimetableSummaryCardsProps) {
  const cards = [
    {
      label: 'Total Subjects',
      value: totalSubjects,
      subtext: 'Curriculum subjects',
      icon: BookOpen,
      containerClass:
        'card bg-indigo-50/70 dark:bg-indigo-950/20 border-indigo-200/80 dark:border-indigo-900/40',
      iconContainerClass:
        'bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border border-indigo-500/25',
      titleClass: 'text-indigo-800 dark:text-indigo-300',
      valueClass: 'text-indigo-600 dark:text-indigo-400',
      subtextClass: 'text-indigo-600 dark:text-indigo-400',
    },
    {
      label: 'Total Periods',
      value: totalPeriods,
      subtext: 'Teaching periods/week',
      icon: Clock,
      containerClass:
        'card bg-emerald-50/70 dark:bg-emerald-950/20 border-emerald-200/80 dark:border-emerald-900/40',
      iconContainerClass:
        'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25',
      titleClass: 'text-emerald-800 dark:text-emerald-300',
      valueClass: 'text-emerald-600 dark:text-emerald-400',
      subtextClass: 'text-emerald-600 dark:text-emerald-400',
    },
    {
      label: 'Days in Week',
      value: daysInWeek,
      subtext: 'Working teaching days',
      icon: Calendar,
      containerClass:
        'card bg-amber-50/70 dark:bg-amber-950/20 border-amber-200/80 dark:border-amber-900/40',
      iconContainerClass:
        'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/25',
      titleClass: 'text-amber-800 dark:text-amber-300',
      valueClass: 'text-amber-600 dark:text-amber-400',
      subtextClass: 'text-amber-600 dark:text-amber-400',
    },
    {
      label: 'Available',
      value: attendance,
      subtext: "Periods you're available for",
      icon: Layers,
      containerClass:
        'card bg-blue-50/70 dark:bg-blue-950/20 border-blue-200/80 dark:border-blue-900/40',
      iconContainerClass:
        'bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/25',
      titleClass: 'text-blue-800 dark:text-blue-300',
      valueClass: 'text-blue-600 dark:text-blue-400',
      subtextClass: 'text-blue-600/80 dark:text-blue-400/80',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3.5">
      {cards.map((card) => {
        const IconComponent = card.icon;
        return (
          <div
            key={card.label}
            className={`${card.containerClass} rounded-[14px] sm:rounded-[16px] p-2.5 sm:px-4 sm:py-2.5 flex items-center gap-2.5 sm:gap-3.5 shadow-2xs`}
          >
            <span
              className={`grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-[10px] sm:rounded-[12px] ${card.iconContainerClass}`}
            >
              <IconComponent className="h-4.5 w-4.5 sm:h-5 sm:w-5" />
            </span>
            <div className="min-w-0 flex flex-col justify-center">
              <span
                className={`text-[12px] sm:text-[14px] font-bold ${card.titleClass} tracking-wide block truncate`}
              >
                {card.label}
              </span>
              <p className={`text-2xl font-black ${card.valueClass} mt-0.5 leading-tight`}>
                {typeof card.value === 'number' ? (card.value === 0 ? '0' : String(card.value).padStart(2, '0')) : card.value}
              </p>
              <span
                className={`text-2xs sm:text-xs ${card.subtextClass} font-semibold mt-0.5 block truncate`}
              >
                {card.subtext}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
